import { supabase } from './supabase';

export interface CurriculumSystem {
  id: string;
  country_code: string;
  system_name: string;
  api_endpoint?: string;
  api_key_required: boolean;
  is_active: boolean;
}

export interface CurriculumLevel {
  id: string;
  curriculum_system_id: string;
  level_code: string;
  level_name: string;
  age_range_start: number;
  age_range_end: number;
  grade_equivalent?: string;
  description?: string;
}

export interface CurriculumStandards {
  id: string;
  curriculum_level_id: string;
  subject_area: string;
  learning_objectives: string[];
  assessment_criteria: string[];
  cached_at: string;
  expires_at: string;
}

export interface CurriculumSelection {
  systemId: string;
  levelId: string;
  countryCode: string;
  autoDetected: boolean;
}

// Subject area mappings across different curriculum systems
export const SUBJECT_MAPPINGS: Record<string, Record<string, string[]>> = {
  GBR: {
    math: ['Mathematics', 'Numeracy'],
    reading: ['English', 'Literacy', 'Reading'],
    science: ['Science', 'Physics', 'Chemistry', 'Biology'],
    social: ['History', 'Geography', 'Citizenship'],
    focus: ['PSHE', 'Personal Development', 'Wellbeing']
  },
  AUS: {
    math: ['Mathematics', 'Numeracy'],
    reading: ['English', 'Literacy'],
    science: ['Science', 'Technologies'],
    social: ['Humanities and Social Sciences', 'History', 'Geography'],
    focus: ['Health and Physical Education', 'Personal and Social Capability']
  },
  USA: {
    math: ['Mathematics', 'Math'],
    reading: ['English Language Arts', 'ELA', 'Reading', 'Literature'],
    science: ['Science', 'STEM', 'Next Generation Science Standards'],
    social: ['Social Studies', 'History', 'Civics'],
    focus: ['Social Emotional Learning', 'SEL', 'Character Education']
  },
  CAN: {
    math: ['Mathematics', 'Math'],
    reading: ['Language Arts', 'English', 'French'],
    science: ['Science', 'STEM'],
    social: ['Social Studies', 'History', 'Geography'],
    focus: ['Health and Life Skills', 'Personal Development']
  },
  NZL: {
    math: ['Mathematics and Statistics'],
    reading: ['English', 'Te Reo Māori'],
    science: ['Science', 'Technology'],
    social: ['Social Sciences'],
    focus: ['Health and Physical Education']
  }
};

// Country code to name mapping
export const COUNTRY_NAMES: Record<string, string> = {
  GBR: 'United Kingdom',
  AUS: 'Australia',
  USA: 'United States',
  CAN: 'Canada',
  NZL: 'New Zealand'
};

// Age-based curriculum level detection
export const detectCurriculumLevel = (age: number, countryCode: string): string | null => {
  const ageRanges: Record<string, Record<string, [number, number]>> = {
    GBR: {
      'KS1': [5, 7],
      'KS2': [7, 11],
      'KS3': [11, 14],
      'KS4': [14, 16],
      'KS5': [16, 18]
    },
    AUS: {
      'FOUNDATION': [5, 6],
      'YEAR1-2': [6, 8],
      'YEAR3-4': [8, 10],
      'YEAR5-6': [10, 12],
      'YEAR7-8': [12, 14],
      'YEAR9-10': [14, 16]
    },
    USA: {
      'K': [5, 6],
      '1-2': [6, 8],
      '3-5': [8, 11],
      '6-8': [11, 14],
      '9-12': [14, 18]
    },
    CAN: {
      'K': [5, 6],
      '1-3': [6, 9],
      '4-6': [9, 12],
      '7-9': [12, 15],
      '10-12': [15, 18]
    },
    NZL: {
      'LEVEL1': [5, 7],
      'LEVEL2': [7, 9],
      'LEVEL3': [9, 11],
      'LEVEL4': [11, 13],
      'LEVEL5': [13, 15],
      'LEVEL6': [15, 17]
    }
  };

  const countryRanges = ageRanges[countryCode];
  if (!countryRanges) return null;

  for (const [levelCode, [minAge, maxAge]] of Object.entries(countryRanges)) {
    if (age >= minAge && age <= maxAge) {
      return levelCode;
    }
  }

  return null;
};

// API client for curriculum data
export class CurriculumAPIClient {
  private static instance: CurriculumAPIClient;
  private cache: Map<string, { data: any; expires: number }> = new Map();

  static getInstance(): CurriculumAPIClient {
    if (!this.instance) {
      this.instance = new CurriculumAPIClient();
    }
    return this.instance;
  }

  private getCacheKey(endpoint: string, params: Record<string, any>): string {
    return `${endpoint}?${new URLSearchParams(params).toString()}`;
  }

  private isExpired(timestamp: number): boolean {
    return Date.now() > timestamp;
  }

  async fetchCurriculumStandards(
    systemId: string,
    levelId: string,
    subjectArea: string,
    useCache: boolean = true
  ): Promise<any> {
    // First check local database cache
    const cachedStandards = await this.getCachedStandards(levelId, subjectArea);
    if (cachedStandards && useCache && !this.isExpired(new Date(cachedStandards.expires_at).getTime())) {
      return {
        learning_objectives: cachedStandards.learning_objectives,
        assessment_criteria: cachedStandards.assessment_criteria,
        source: 'database_cache'
      };
    }

    // Try to fetch from external API
    try {
      const system = await this.getCurriculumSystem(systemId);
      if (system?.api_endpoint) {
        const apiData = await this.fetchFromAPI(system.api_endpoint, levelId, subjectArea);
        
        // Cache the API response in database
        await this.cacheStandards(levelId, subjectArea, apiData);
        return { ...apiData, source: 'api' };
      }
    } catch (error) {
      console.warn('Failed to fetch from curriculum API:', error);
    }

    // Fallback to static data
    return this.getStaticCurriculumData(systemId, levelId, subjectArea);
  }

  private async getCurriculumSystem(systemId: string): Promise<CurriculumSystem | null> {
    const { data, error } = await supabase
      .from('curriculum_systems')
      .select('*')
      .eq('id', systemId)
      .single();

    if (error) {
      console.error('Error fetching curriculum system:', error);
      return null;
    }

    return data;
  }

  private async getCachedStandards(levelId: string, subjectArea: string): Promise<CurriculumStandards | null> {
    const { data, error } = await supabase
      .from('curriculum_standards')
      .select('*')
      .eq('curriculum_level_id', levelId)
      .eq('subject_area', subjectArea)
      .single();

    if (error || !data) return null;
    return data;
  }

  private async cacheStandards(
    levelId: string,
    subjectArea: string,
    standards: { learning_objectives: string[]; assessment_criteria: string[] }
  ): Promise<void> {
    const { error } = await supabase
      .from('curriculum_standards')
      .upsert({
        curriculum_level_id: levelId,
        subject_area: subjectArea,
        learning_objectives: standards.learning_objectives,
        assessment_criteria: standards.assessment_criteria,
        cached_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
      }, {
        onConflict: 'curriculum_level_id,subject_area'
      });

    if (error) {
      console.error('Error caching curriculum standards:', error);
    }
  }

  private async fetchFromAPI(endpoint: string, levelId: string, subjectArea: string): Promise<any> {
    // This would be implemented based on specific API requirements
    // For now, return a mock structure
    return {
      learning_objectives: [
        `${subjectArea} learning objective for ${levelId}`,
        `Advanced ${subjectArea} concepts for ${levelId}`,
        `Assessment-ready ${subjectArea} skills for ${levelId}`
      ],
      assessment_criteria: [
        `Can demonstrate understanding of ${subjectArea} concepts`,
        `Applies ${subjectArea} knowledge to practical situations`,
        `Shows progression in ${subjectArea} skills`
      ]
    };
  }

  private getStaticCurriculumData(systemId: string, levelId: string, subjectArea: string): any {
    // Static fallback data structure
    const staticData = {
      learning_objectives: [
        `Core ${subjectArea} concepts appropriate for this level`,
        `Essential ${subjectArea} skills development`,
        `Progressive ${subjectArea} understanding`
      ],
      assessment_criteria: [
        `Demonstrates understanding of key ${subjectArea} concepts`,
        `Shows appropriate skill level for age group`,
        `Can apply learning in practical contexts`
      ],
      source: 'static_fallback'
    };

    return staticData;
  }
}

// Helper functions for curriculum operations
export const getCurriculumSystems = async (): Promise<CurriculumSystem[]> => {
  const { data, error } = await supabase
    .from('curriculum_systems')
    .select('*')
    .eq('is_active', true)
    .order('system_name');

  if (error) {
    console.error('Error fetching curriculum systems:', error);
    return [];
  }

  return data || [];
};

export const getCurriculumLevels = async (systemId: string): Promise<CurriculumLevel[]> => {
  const { data, error } = await supabase
    .from('curriculum_levels')
    .select('*')
    .eq('curriculum_system_id', systemId)
    .order('age_range_start');

  if (error) {
    console.error('Error fetching curriculum levels:', error);
    return [];
  }

  return data || [];
};

export const getCurriculumLevelForAge = async (
  age: number, 
  systemId: string
): Promise<CurriculumLevel | null> => {
  const { data, error } = await supabase
    .rpc('get_curriculum_level_for_age', {
      p_age: age,
      p_curriculum_system_id: systemId
    });

  if (error || !data) {
    console.error('Error getting curriculum level for age:', error);
    return null;
  }

  return data;
};

export const getSubjectAreasForSystem = (countryCode: string): string[] => {
  const subjects = SUBJECT_MAPPINGS[countryCode];
  if (!subjects) return ['math', 'reading', 'science', 'social', 'focus'];
  
  return Object.keys(subjects);
};

export const mapSubjectToSystemTerms = (subject: string, countryCode: string): string[] => {
  const subjects = SUBJECT_MAPPINGS[countryCode];
  if (!subjects || !subjects[subject]) {
    return [subject]; // Fallback to original subject name
  }
  
  return subjects[subject];
};

// Curriculum context for AI generation
export const buildCurriculumContext = async (
  curriculumSelection: CurriculumSelection,
  subjects: string[]
): Promise<string> => {
  const system = await supabase
    .from('curriculum_systems')
    .select('*')
    .eq('id', curriculumSelection.systemId)
    .single();

  const level = await supabase
    .from('curriculum_levels')
    .select('*')
    .eq('id', curriculumSelection.levelId)
    .single();

  if (system.error || level.error) {
    return 'Generate activities appropriate for the child\'s age and learning level.';
  }

  const systemName = system.data.system_name;
  const levelName = level.data.level_name;
  const ageRange = `${level.data.age_range_start}-${level.data.age_range_end} years`;
  
  const subjectTerms = subjects
    .map(subject => mapSubjectToSystemTerms(subject, curriculumSelection.countryCode))
    .flat()
    .join(', ');

  return `Generate activities aligned with the ${systemName} for ${levelName} (ages ${ageRange}). ` +
    `Focus on subjects: ${subjectTerms}. Use appropriate terminology and learning expectations ` +
    `for this curriculum level. Ensure activities meet the standards and assessment criteria ` +
    `expected for students at this stage of education.`;
};